import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchParams } from '@savgroup-front-common/core/hooks';

import { FINAL_ROUTES } from '@/view/app';
import { useGetModelByIdQuery } from '@/view/app/hooks/useGetModelByIdQuery';
import { useGetMultipleCustomerFileFullSummaryQuery } from '@/view/app/hooks/useGetMultipleCustomerFileFullSummaryQuery';
import { useGetMultipleFilesByOrderQuery } from '@/view/app/hooks/useGetMultipleFilesByOrderQuery';
import { useGetMultipleFileStatesQuery } from '@/view/app/hooks/useGetMultipleFileStatesQuery';
import { useGetMultipleSolutionTypeQuery } from '@/view/app/hooks/useGetMultipleSolutionTypeQuery';
import { useGetOrderByOwnerId } from '@/view/app/hooks/useGetOrderByOwnerId';
import { buildFileFollowupUrl } from '@/view/app/NewLayout/ClassiqueLayout.helpers';
import { useLayout } from '@/view/app/NewLayout/NewLayout.hooks';

import { buildIrshSearchParams } from '../irshPages/helpers/buildIrshSearchParams';
import { IRSH_QUERY_PARAMS } from '../irshPages/IrshPages.constants';

import {
  adaptAllProducts,
  adaptMultiCustomerFileFullSummary,
} from './OrdersPage.adapters';

export const useOrdersPage = () => {
  const { ownerId } = useLayout();
  const navigate = useNavigate();
  const query = useSearchParams();

  const issueUniqueNameFromUrl =
    query.get(IRSH_QUERY_PARAMS.ISSUE) ?? undefined;
  const reasonNameFromUrl = query.get(IRSH_QUERY_PARAMS.REASON) ?? undefined;

  const newRequestRoute = `${FINAL_ROUTES.ISSUES}${buildIrshSearchParams({
    issueUniqueName: issueUniqueNameFromUrl,
    reasonName: reasonNameFromUrl,
  })}`;
  const { orders } = useGetOrderByOwnerId({ ownerId });
  const { ordersDetail, isLoading: isLoadingOrderDetail } =
    useGetMultipleFilesByOrderQuery({
      orderIds: orders ? orders.map((order) => order.orderId) : undefined,
    });

  const fileIds = ordersDetail?.map((fileSummary) => fileSummary.fileId);
  const { multiProductsInfo, refetchFileSummary } =
    useGetMultipleCustomerFileFullSummaryQuery({
      fileIds: !isLoadingOrderDetail ? fileIds : undefined,
    });

  const multiProductsInfoAdapted =
    adaptMultiCustomerFileFullSummary(multiProductsInfo);

  const { models } = useGetModelByIdQuery({
    productsInfo: multiProductsInfoAdapted?.flat(1),
  });

  const { solutions } = useGetMultipleSolutionTypeQuery({
    solutionTypeIds: multiProductsInfoAdapted
      ?.flat(1)
      .map((productInfo) => productInfo?.solutionId),
  });

  const { multiHistories, refetchHistories } = useGetMultipleFileStatesQuery({
    fileIds: !isLoadingOrderDetail ? fileIds : undefined,
  });

  useEffect(() => {
    refetchFileSummary();
    refetchHistories();
  }, [isLoadingOrderDetail, refetchFileSummary, refetchHistories]);

  const handleShowProduct = ({ fileId }: { fileId?: string }) => {
    if (!fileId) {
      return;
    }

    navigate(buildFileFollowupUrl({ fileId }));
  };

  return {
    productsInfoAndHistoryList: adaptAllProducts({
      models,
      solutions,
      multiProductsInfoAdapted,
      multiHistories,
    }),
    handleShowProduct,
    newRequestRoute,
  };
};
